// Shared utilities for Corporate, Doctor, and Hospital dashboards

/**
 * Export data to CSV file
 * @param {Array} headers - Column headers
 * @param {Array} rows - Array of arrays containing row data
 * @param {string} filename - Download filename
 */
export function exportToCSV(headers, rows, filename) {
  const csvContent = [
    headers.join(','),
    ...rows.map(row =>
      row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')
    )
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Prepare session trend data from sessions array
 * @param {Array} sessions - Array of session objects with score fields
 * @param {Object} options - Options for data preparation
 * @returns {Array} Array of trend data objects
 */
export function prepareSessionTrendData(sessions, options = {}) {
  const { includeWellbeing = false, sortAscending = false } = options;
  
  if (!sessions || !Array.isArray(sessions) || sessions.length === 0) return [];
  
  const trendData = sessions
    .filter(s => s.emotion_score != null)
    .map(s => {
      const data = {
        date: s.session_date ? new Date(s.session_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'N/A',
        emotion: parseFloat(s.emotion_score) || 0,
        motivation: parseFloat(s.motivation_score) || 0,
        burnout: parseFloat(s.burnout_score) || 0,
        engagement: parseFloat(s.engagement_score) || 0,
        stress: parseFloat(s.stress_score) || 0
      };
      
      if (includeWellbeing) {
        data.wellbeing = parseFloat(s.wellbeing_score) || 0;
        data.fullDate = s.session_date;
      }
      
      return data;
    });
  
  if (sortAscending) {
    return trendData.sort((a, b) => new Date(a.fullDate || 0) - new Date(b.fullDate || 0));
  }
  
  return trendData.reverse();
}

/**
 * Generate session flags from employee sessions
 * @param {Array} sessions - Array of session objects
 * @returns {Array} Array of flag objects
 */
export function generateSessionFlags(sessions) {
  const flags = [];
  if (!sessions || !Array.isArray(sessions)) return flags;

  sessions.forEach(session => {
    const burnout = parseFloat(session.burnout_score) || 0;
    const stress = parseFloat(session.stress_score) || 0;
    const motivation = parseFloat(session.motivation_score) || 0;
    const emotion = parseFloat(session.emotion_score) || 0;

    if (burnout > 70) {
      flags.push({
        date: session.session_date ? new Date(session.session_date).toLocaleDateString() : 'N/A',
        type: 'High Burnout Risk',
        severity: 'high',
        resolved: false
      });
    }
    if (stress > 75) {
      flags.push({
        date: session.session_date ? new Date(session.session_date).toLocaleDateString() : 'N/A',
        type: 'High Stress Level',
        severity: 'high',
        resolved: false
      });
    }
    if (motivation < 30) {
      flags.push({
        date: session.session_date ? new Date(session.session_date).toLocaleDateString() : 'N/A',
        type: 'Low Motivation',
        severity: 'medium',
        resolved: false
      });
    }
    if (emotion < 30) {
      flags.push({
        date: session.session_date ? new Date(session.session_date).toLocaleDateString() : 'N/A',
        type: 'Low Emotion Score',
        severity: 'medium',
        resolved: false
      });
    }
  });

  return flags.sort((a, b) => new Date(b.date) - new Date(a.date));
}

/**
 * Generate coaching suggestions from session data
 * @param {Array} sessions - Array of session objects
 * @returns {Array} Array of suggestion strings
 */
export function generateSessionSuggestions(sessions) {
  const suggestions = [];
  if (!sessions || !Array.isArray(sessions) || sessions.length === 0) {
    suggestions.push('Conduct a session to get personalized coaching suggestions.');
    return suggestions;
  }

  const latestSession = sessions[0];
  const burnout = parseFloat(latestSession.burnout_score) || 0;
  const stress = parseFloat(latestSession.stress_score) || 0;
  const motivation = parseFloat(latestSession.motivation_score) || 0;
  const emotion = parseFloat(latestSession.emotion_score) || 0;
  const engagement = parseFloat(latestSession.engagement_score) || 0;
  const wellbeing = parseFloat(latestSession.wellbeing_score) || 0;

  if (burnout > 70) {
    suggestions.push('🔥 High burnout detected: Recommend taking time off, reducing workload, and engaging in stress-relief activities.');
  } else if (burnout > 50) {
    suggestions.push('⚠️ Moderate burnout risk: Consider work-life balance improvements and regular breaks.');
  }

  if (stress > 75) {
    suggestions.push('😰 High stress level: Suggest mindfulness exercises, deep breathing, and time management strategies.');
  } else if (stress > 50) {
    suggestions.push('⚠️ Elevated stress: Recommend stress management techniques and regular exercise.');
  }

  if (motivation < 30) {
    suggestions.push('📉 Low motivation: Consider goal-setting sessions, recognition programs, and career development opportunities.');
  } else if (motivation < 50) {
    suggestions.push('📊 Motivation could improve: Suggest team-building activities and clear communication of goals.');
  }

  if (emotion < 30) {
    suggestions.push('😔 Low emotional well-being: Recommend counseling support, peer connections, and emotional wellness programs.');
  }

  if (engagement < 50) {
    suggestions.push('💼 Low engagement: Consider involving in decision-making, providing meaningful work, and regular feedback.');
  }

  if (wellbeing < 50) {
    suggestions.push('🏥 Overall wellbeing needs attention: Focus on holistic health - physical, mental, and emotional support.');
  }

  if (motivation > 70 && emotion > 60 && burnout < 40) {
    suggestions.push('✅ Great performance! Continue maintaining work-life balance and stay engaged.');
  }

  if (suggestions.length === 0) {
    suggestions.push('✅ All metrics are within healthy ranges. Keep up the good work!');
  }

  return suggestions;
}

/**
 * Get burnout risk level from a percentage or employee object
 * @param {number|Object|null} input - Burnout percentage or employee object with risk data
 * @returns {Object} Risk level with label and colors
 */
export function getBurnoutRiskLevel(input) {
  if (input === null || input === undefined) {
    return { level: 'no-data', label: 'No Data', color: '#6b7280', bgColor: '#f3f4f6' };
  }

  let percentage;
  if (typeof input === 'number') {
    percentage = input;
  } else {
    if (!input.has_data || input.finalRiskScore === null || input.finalRiskScore === undefined) {
      return { level: 'no-data', label: 'No Data', color: '#6b7280', bgColor: '#f3f4f6' };
    }
    if (input.burnoutFlag) {
      return { level: 'high', label: 'High', color: '#ef4444', bgColor: '#fee2e2' };
    }
    percentage = input.finalRiskScore || 0;
  }

  if (percentage >= 70) return { level: 'high', label: 'High', color: '#ef4444', bgColor: '#fee2e2' };
  if (percentage >= 50) return { level: 'moderate', label: 'Moderate', color: '#f59e0b', bgColor: '#fef3c7' };
  return { level: 'low', label: 'Low', color: '#10b981', bgColor: '#d1fae5' };
}

/**
 * Get employee burnout risk level based on risk score and burnout flag
 * @param {Object} employee - Employee object with risk data
 * @returns {Object} Risk level with label and colors
 */
export function getEmployeeBurnoutRisk(employee) {
  return getBurnoutRiskLevel(employee);
}


